import type { AssignmentScope } from '@Application/Ai/AiConfiguration/Assignments/AssignmentScope';
import type { ModelAssignmentClearedAction } from '@Application/Ai/AiConfiguration/Assignments/Actions/modelAssignmentCleared';
import type { ModelAssignmentSavedAction } from '@Application/Ai/AiConfiguration/Assignments/Actions/modelAssignmentSaved';
import type { ModelAssignmentsFetchedAction } from '@Application/Ai/AiConfiguration/Assignments/Actions/modelAssignmentsFetched';
import type { UsageSettingsSavedAction } from '@Application/Ai/AiConfiguration/Assignments/Actions/usageSettingsSaved';
import type { AiModelConnectionTestedAction } from '@Application/Ai/AiConfiguration/Models/Actions/aiModelConnectionTested';
import type { AiModelCreatedAction } from '@Application/Ai/AiConfiguration/Models/Actions/aiModelCreated';
import type { AiModelDeletedAction } from '@Application/Ai/AiConfiguration/Models/Actions/aiModelDeleted';
import type { AiModelUpdatedAction } from '@Application/Ai/AiConfiguration/Models/Actions/aiModelUpdated';
import type { AiModelsFetchedAction } from '@Application/Ai/AiConfiguration/Models/Actions/aiModelsFetched';
import type { AiProviderConnectionTestedAction } from '@Application/Ai/AiConfiguration/Providers/Actions/aiProviderConnectionTested';
import type { AiProviderCreatedAction } from '@Application/Ai/AiConfiguration/Providers/Actions/aiProviderCreated';
import type { AiProviderDeletedAction } from '@Application/Ai/AiConfiguration/Providers/Actions/aiProviderDeleted';
import type { AiProviderUpdatedAction } from '@Application/Ai/AiConfiguration/Providers/Actions/aiProviderUpdated';
import type { AiProvidersFetchedAction } from '@Application/Ai/AiConfiguration/Providers/Actions/aiProvidersFetched';
import type { UserRevokedAction } from '@Application/Security/Authentications/Actions/userRevoked';
import AiModelView from '@UI/Store/AiConfiguration/Views/AiModelView';
import AiModelsListView from '@UI/Store/AiConfiguration/Views/AiModelsListView';
import AiProviderView from '@UI/Store/AiConfiguration/Views/AiProviderView';
import AiProvidersListView from '@UI/Store/AiConfiguration/Views/AiProvidersListView';
import ModelAssignmentsListView from '@UI/Store/AiConfiguration/Views/ModelAssignmentsListView';
import ModelAssignmentView from '@UI/Store/AiConfiguration/Views/ModelAssignmentView';
import ModelConnectionTestView from '@UI/Store/AiConfiguration/Views/ModelConnectionTestView';
import ProviderConnectionTestView from '@UI/Store/AiConfiguration/Views/ProviderConnectionTestView';
import { createSliceReducer, registerStore } from '@verclam/synapse';

export type AiConfigurationStore = {
	providers: AiProvidersListView | null;
	models: AiModelsListView | null;
	accountAssignments: ModelAssignmentsListView | null;
	organizationAssignments: ModelAssignmentsListView | null;
	facilityAssignments: ModelAssignmentsListView | null;
	lastProviderConnectionTest: ProviderConnectionTestView | null;
	lastModelConnectionTest: ModelConnectionTestView | null;
};

const initialState: AiConfigurationStore = {
	providers: null,
	models: null,
	accountAssignments: null,
	organizationAssignments: null,
	facilityAssignments: null,
	lastProviderConnectionTest: null,
	lastModelConnectionTest: null,
};

type Actions =
	| AiProvidersFetchedAction
	| AiProviderCreatedAction
	| AiProviderUpdatedAction
	| AiProviderDeletedAction
	| AiProviderConnectionTestedAction
	| AiModelsFetchedAction
	| AiModelCreatedAction
	| AiModelUpdatedAction
	| AiModelDeletedAction
	| AiModelConnectionTestedAction
	| ModelAssignmentsFetchedAction
	| ModelAssignmentSavedAction
	| ModelAssignmentClearedAction
	| UsageSettingsSavedAction
	| UserRevokedAction;

type AssignmentsKey = 'accountAssignments' | 'organizationAssignments' | 'facilityAssignments';

const assignmentsKeyFor = (scopeType: AssignmentScope['type']): AssignmentsKey => {
	switch (scopeType) {
		case 'account':
			return 'accountAssignments';
		case 'organization':
			return 'organizationAssignments';
		case 'facility':
			return 'facilityAssignments';
	}
};

const scopeIdOf = (scope: AssignmentScope): number => {
	switch (scope.type) {
		case 'account':
			return scope.accountId;
		case 'organization':
			return scope.organizationId;
		case 'facility':
			return scope.facilityId;
	}
};

const whenAiProvidersFetched = (
	store: AiConfigurationStore,
	action: AiProvidersFetchedAction,
): AiConfigurationStore => ({
	...store,
	providers: new AiProvidersListView(
		action.payload.map((entry) => new AiProviderView(entry)),
		action.accountId,
	),
});

const whenAiProviderCreated = (
	store: AiConfigurationStore,
	action: AiProviderCreatedAction,
): AiConfigurationStore => {
	if (store.providers === null || store.providers.scopeId !== action.accountId) {
		return store;
	}

	return { ...store, providers: store.providers.add(new AiProviderView(action.payload)) };
};

const whenAiProviderUpdated = (
	store: AiConfigurationStore,
	action: AiProviderUpdatedAction,
): AiConfigurationStore => {
	if (store.providers === null || store.providers.scopeId !== action.accountId) {
		return store;
	}

	return { ...store, providers: store.providers.update(new AiProviderView(action.payload)) };
};

const whenAiProviderDeleted = (
	store: AiConfigurationStore,
	action: AiProviderDeletedAction,
): AiConfigurationStore => {
	if (store.providers === null || store.providers.scopeId !== action.accountId) {
		return store;
	}

	return { ...store, providers: store.providers.remove(action.payload.providerId) };
};

const whenAiProviderConnectionTested = (
	store: AiConfigurationStore,
	action: AiProviderConnectionTestedAction,
): AiConfigurationStore => {
	const lastProviderConnectionTest = new ProviderConnectionTestView(action.payload);

	if (store.providers === null || store.providers.scopeId !== action.accountId) {
		return { ...store, lastProviderConnectionTest };
	}

	const provider = store.providers.findById(action.providerId);

	if (provider === null) {
		return { ...store, lastProviderConnectionTest };
	}

	return {
		...store,
		lastProviderConnectionTest,
		providers: store.providers.update(
			provider.withConnectionTestResult(
				action.payload.succeeded,
				action.payload.testedAt,
				action.payload.message,
				action.payload.models,
			),
		),
	};
};

const whenAiModelsFetched = (
	store: AiConfigurationStore,
	action: AiModelsFetchedAction,
): AiConfigurationStore => ({
	...store,
	models: new AiModelsListView(
		action.payload.map((entry) => new AiModelView(entry)),
		action.accountId,
	),
});

const whenAiModelCreated = (
	store: AiConfigurationStore,
	action: AiModelCreatedAction,
): AiConfigurationStore => {
	if (store.models === null || store.models.scopeId !== action.accountId) {
		return store;
	}

	return { ...store, models: store.models.add(new AiModelView(action.payload)) };
};

const whenAiModelUpdated = (
	store: AiConfigurationStore,
	action: AiModelUpdatedAction,
): AiConfigurationStore => {
	if (store.models === null || store.models.scopeId !== action.accountId) {
		return store;
	}

	return { ...store, models: store.models.update(new AiModelView(action.payload)) };
};

const whenAiModelDeleted = (
	store: AiConfigurationStore,
	action: AiModelDeletedAction,
): AiConfigurationStore => {
	if (store.models === null || store.models.scopeId !== action.accountId) {
		return store;
	}

	return { ...store, models: store.models.remove(action.payload.modelId) };
};

const whenAiModelConnectionTested = (
	store: AiConfigurationStore,
	action: AiModelConnectionTestedAction,
): AiConfigurationStore => {
	const lastModelConnectionTest = new ModelConnectionTestView(action.payload);

	if (store.models === null || store.models.scopeId !== action.accountId) {
		return { ...store, lastModelConnectionTest };
	}

	const model = store.models.findById(action.modelId);

	if (model === null) {
		return { ...store, lastModelConnectionTest };
	}

	return {
		...store,
		lastModelConnectionTest,
		models: store.models.update(
			model.withConnectionTestResult(
				action.payload.succeeded,
				action.payload.testedAt,
				action.payload.message,
				action.payload.usage,
				action.payload.rejectedSettings,
			),
		),
	};
};

const whenModelAssignmentsFetched = (
	store: AiConfigurationStore,
	action: ModelAssignmentsFetchedAction,
): AiConfigurationStore => {
	const key = assignmentsKeyFor(action.scope.type);

	return {
		...store,
		[key]: new ModelAssignmentsListView(
			action.payload.map((entry) => new ModelAssignmentView(entry)),
			scopeIdOf(action.scope),
			action.scope.type,
		),
	};
};

const whenModelAssignmentSaved = (
	store: AiConfigurationStore,
	action: ModelAssignmentSavedAction,
): AiConfigurationStore => {
	const key = assignmentsKeyFor(action.scope.type);
	const current = store[key];

	if (current === null || current.scopeId !== scopeIdOf(action.scope)) {
		return store;
	}

	return {
		...store,
		[key]: current.replaceUsage(action.payload.usage.value, new ModelAssignmentView(action.payload)),
	};
};

const whenUsageSettingsSaved = (
	store: AiConfigurationStore,
	action: UsageSettingsSavedAction,
): AiConfigurationStore => {
	const key = assignmentsKeyFor(action.scope.type);
	const current = store[key];

	if (current === null || current.scopeId !== scopeIdOf(action.scope)) {
		return store;
	}

	return {
		...store,
		[key]: current.replaceUsage(action.payload.usage.value, new ModelAssignmentView(action.payload)),
	};
};

// The DELETE responds 204 with no body: removing an override makes the inherited value reappear, and
// the front cannot compute it locally without necessarily having loaded the levels above. Resetting
// the slot to `null` is the documented invalidation mechanism (`state-stores.md`, "Étendre un store"):
// `stopOn` turns false and `useStoreQuery` refetches. No inheritance is computed client-side.
const whenModelAssignmentCleared = (
	store: AiConfigurationStore,
	action: ModelAssignmentClearedAction,
): AiConfigurationStore => {
	const key = assignmentsKeyFor(action.scope.type);
	const current = store[key];

	if (current === null || current.scopeId !== scopeIdOf(action.scope)) {
		return store;
	}

	return { ...store, [key]: null };
};

// Provider and model catalogs carry provider names and internal account URLs: they must not survive
// a logout and be served to the next person on the same browser.
const whenUserRevoked = (): AiConfigurationStore => initialState;

export default registerStore<'aiConfigurationStore', Actions>(
	'aiConfigurationStore',
	createSliceReducer({
		AI_PROVIDERS_FETCHED: whenAiProvidersFetched,
		AI_PROVIDER_CREATED: whenAiProviderCreated,
		AI_PROVIDER_UPDATED: whenAiProviderUpdated,
		AI_PROVIDER_DELETED: whenAiProviderDeleted,
		AI_PROVIDER_CONNECTION_TESTED: whenAiProviderConnectionTested,
		AI_MODELS_FETCHED: whenAiModelsFetched,
		AI_MODEL_CREATED: whenAiModelCreated,
		AI_MODEL_UPDATED: whenAiModelUpdated,
		AI_MODEL_DELETED: whenAiModelDeleted,
		AI_MODEL_CONNECTION_TESTED: whenAiModelConnectionTested,
		MODEL_ASSIGNMENTS_FETCHED: whenModelAssignmentsFetched,
		MODEL_ASSIGNMENT_SAVED: whenModelAssignmentSaved,
		MODEL_ASSIGNMENT_CLEARED: whenModelAssignmentCleared,
		USAGE_SETTINGS_SAVED: whenUsageSettingsSaved,
		USER_REVOKED: whenUserRevoked,
	}),
	initialState,
);
